<template>
  <v-card>
    <v-card-title class="flex-nowrap">
      <div class="flex-grow-1 flex-shrink-1 pr-4">
        {{ $t(`minimalRequirement.${requirement.requirementId}.name`) }}
      </div>

      <WipSwitch :requirement="requirement" model="minimal" class="mr-2" />

      <WipAuditorSwitch :requirement="requirement" model="minimal" />

      <div>
        <AuditRequirementInfo :requirement="requirement" />
      </div>

      <div class="pl-4">
        <v-btn v-if="!edit" @click="edit = true" color="secondary" icon>
          <v-icon>mdi-pencil</v-icon>
        </v-btn>

        <v-btn v-if="edit" @click="edit = false" icon>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </v-card-title>

    <v-card-text v-if="edit">
      <v-row>
        <v-col cols="4">
          <v-row>
            <v-col cols="12">
              <v-card outlined>
                <v-card-title>
                  {{ $t("auditor.minimalRequirementForm.description") }}
                </v-card-title>

                <v-card-text>
                  <Markdown :value="$t(`minimalRequirement.${requirement.requirementId}.description`)"></Markdown>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" v-if="$t(`minimalRequirement.${requirement.requirementId}.help`).trim().length > 0">
              <v-card outlined>
                <v-card-title>
                  {{
                    fund.auditPeriod.year > 2024
                      ? $t("auditor.minimalRequirementForm.help2025")
                      : $t("auditor.minimalRequirementForm.help")
                  }}
                </v-card-title>

                <v-card-text>
                  <Markdown :value="$t(`minimalRequirement.${requirement.requirementId}.help`)"></Markdown>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" v-if="attachments.length > 0">
              <v-dialog v-model="attachmentsDialog" persistent max-width="600">
                <template #activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" color="secondary">
                    {{ $t("action.downloadAll", { count: attachments.length }) }}
                  </v-btn>
                </template>

                <v-card>
                  <v-card-title class="headline">
                    {{ $t("auditor.download.confirmation.title") }}
                  </v-card-title>

                  <v-card-text>
                    <Markdown :value="$t('auditor.download.confirmation.description')" />
                  </v-card-text>

                  <v-card-actions>
                    <v-spacer></v-spacer>

                    <v-btn color="warning" @click="attachmentsDialog = false">
                      {{ $t("auditor.download.confirmation.cancel") }}
                    </v-btn>

                    <v-btn
                      color="primary"
                      @click="
                        downloadAllAttachments();
                        attachmentsDialog = false;
                      "
                    >
                      {{ $t("auditor.download.confirmation.accept") }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="8">
          <v-row>
            <v-col cols="12" v-if="requirement.userFeedback">
              <v-card outlined>
                <v-card-title>{{ $t("userFeedback.title") }}</v-card-title>

                <v-card-text>
                  <Markdown :value="requirement.userFeedback" />
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" v-if="requirement.userFeedback">
              <MinimalRequirementAuditorFeedbackForm :requirement="requirement" />
            </v-col>

            <v-col v-if="displayReferenceInfo" cols="12">
              <ReferenceInfo :fund="fund" model="minimal" :requirement="requirement" audit />
            </v-col>

            <v-col v-if="displayResult" cols="12">
              <RequirementResultInfo :requirement="requirement" />
            </v-col>

            <v-col cols="12">
              <ResponseList :responses="requirement.protocol" />
            </v-col>

            <v-col cols="12" v-if="requirement.referenced && requirement.pendingResponse">
              {{ $t("pendingResponse.title") }}
              <ResponseList :responses="[requirement.pendingResponse]" />
            </v-col>

            <v-col cols="12" v-if="displayButtons">
              <v-btn color="primary" class="mr-4" @click="showResponseForm" v-if="displayAddResponseButton">
                {{ $t("auditor.minimalRequirementForm.addResponse") }}
              </v-btn>

              <v-btn color="primary" @click="showResultForm">
                {{ $t("auditor.minimalRequirementForm.addResult") }}
              </v-btn>
            </v-col>

            <v-col cols="12" v-if="displayResponseForm">
              <RequirementResponseForm
                type="auditor"
                model="minimal"
                :requirement="requirement"
                @save="responseForm = false"
              />
            </v-col>

            <v-col cols="12" v-if="displayResultForm">
              <MinimalRequirementResultForm :requirement="requirement" :fund="fund" @save="resultForm = false" />
            </v-col>

            <v-col cols="12">
              <v-card outlined>
                <v-card-title>
                  {{ $t("auditor.minimalRequirementForm.implementation") }}
                </v-card-title>

                <v-card-text>
                  <Markdown :value="$t(`minimalRequirement.${requirement.requirementId}.implementation`)"></Markdown>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" v-if="!requirement.referenced">
              <InternalCommentForm model="minimal" :requirement="requirement" />
            </v-col>

            <v-col cols="12" v-if="requirement.referenced">
              <v-card outlined>
                <v-card-title>
                  {{ $t("auditor.internalCommentForm.title") }}
                </v-card-title>

                <v-card-text>
                  {{ requirement.InternalComment }}
                </v-card-text>

                <v-card-title>
                  {{ $t("auditor.notReferencedInternalCommentForm.title") }}
                </v-card-title>

                <v-card-text>
                  {{ requirement.referenceRequirement.notReferencedInternalComment }}
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12">
              <NotReferencedInternalCommentForm model="minimal" :requirement="requirement" />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { saveAs } from "file-saver";
import ResponseList from "@/components/Fund/ResponseList";
import AuditRequirementInfo from "./AuditRequirementInfo";
import WipSwitch from "@/components/Requirement/WipSwitch.vue";
import WipAuditorSwitch from "@/components/Requirement/WipAuditorSwitch.vue";
import ReferenceInfo from "@/components/Requirement/ReferenceInfo";
import MinimalRequirementResultForm from "./MinimalRequirementResultForm";
import InternalCommentForm from "@/components/Requirement/InternalCommentForm";
import NotReferencedInternalCommentForm from "@/components/Requirement/NotReferencedInternalCommentForm";
import RequirementResultInfo from "@/components/Requirement/RequirementResultInfo";
import RequirementResponseForm from "@/components/Requirement/RequirementResponseForm";
import MinimalRequirementAuditorFeedbackForm from "./MinimalRequirementAuditorFeedbackForm.vue";
import { flatten, pluck } from "ramda";

export default {
  components: {
    WipSwitch,
    WipAuditorSwitch,
    ResponseList,
    ReferenceInfo,
    InternalCommentForm,
    AuditRequirementInfo,
    RequirementResultInfo,
    RequirementResponseForm,
    MinimalRequirementResultForm,
    NotReferencedInternalCommentForm,
    MinimalRequirementAuditorFeedbackForm,
  },

  props: {
    requirement: {
      type: Object,
      required: true,
    },

    fund: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      edit: false,
      resultForm: false,
      responseForm: false,
      attachmentsDialog: false,
    };
  },

  computed: {
    displayResult() {
      return this.requirement.state === "DONE";
    },

    displayButtons() {
      return !this.requirement.referenced && this.requirement.state === "AUDIT";

      // TODO brauchen wir das?
      // if (this.fund.state === "MINIMAL_REQUIREMENTS_DONE") return false;
    },

    displayResponseForm() {
      if (this.requirement.referenced) return false;
      //if (this.fund.state === "MINIMAL_REQUIREMENTS_DONE") return false;
      if (this.requirement.state === "EDIT") return true;
      if (this.requirement.state === "AUDIT" && this.responseForm) return true;
      return false;
    },

    displayResultForm() {
      if (this.requirement.referenced) return false;
      if (this.requirement.state === "DONE") return true;
      if (this.requirement.state === "AUDIT" && this.resultForm) return true;
      // if (this.fund.state === "MINIMAL_REQUIREMENTS_DONE") return true;
      return false;
    },

    displayAddResponseButton() {
      // TODO: need final audit?
      return this.fund.minimalRequirementsState === "AUDIT";
    },

    attachments() {
      return flatten(pluck("attachments", this.requirement.protocol));
    },

    displayReferenceInfo() {
      return (
        this.requirement.referenced ||
        (this.requirement.referenceRequirement &&
          this.requirement.referenceRequirement.result &&
          this.requirement.referenceRequirement.fund.auditPeriod.year !== new Date().getFullYear())
      );
    },
  },

  methods: {
    showResponseForm() {
      this.responseForm = true;
      this.resultForm = false;
    },

    showResultForm() {
      this.responseForm = false;
      this.resultForm = true;
    },

    downloadAllAttachments() {
      this.download(this.attachments);
    },

    download(attachments) {
      attachments.forEach((attachment, index) => {
        setTimeout(
          () =>
            fetch(attachment.file.url)
              .then((res) => res.blob())
              .then((blob) => {
                saveAs(blob, attachment.file.name);
              }),
          index * 500
        );
      });
    },
  },
};
</script>
